/**
 * User Transactions Component
 * View transactions for companies the user has access to (read-only, no sync)
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import '../styles/UserTransactions.css';

function UserTransactions() {
  // Company state
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');

  // Transaction state
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 50;

  // Filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Data for filters
  const [departments, setDepartments] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editDepartment, setEditDepartment] = useState('');
  const [editComment, setEditComment] = useState('');

  // New department inline
  const [showNewDeptInput, setShowNewDeptInput] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');

  // Edit department
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editDeptName, setEditDeptName] = useState('');

  // Department Analytics
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Load user's companies
  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/data/my-companies');
      const companyList = response.data || [];
      setCompanies(companyList);

      // Auto-select first company if available
      if (companyList.length > 0) {
        setSelectedCompanyId(companyList[0].company_id);
        setSelectedCompanyName(companyList[0].company_name);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  // Load transactions
  const loadTransactions = useCallback(async () => {
    if (!selectedCompanyId) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (transactionType) params.append('transaction_type', transactionType);
      if (departmentFilter) {
        if (departmentFilter === 'none') {
          params.append('has_department', 'false');
        } else {
          params.append('department_id', departmentFilter);
        }
      }
      if (searchTerm) params.append('search', searchTerm);
      if (selectedBankAccount) params.append('account_name', selectedBankAccount);

      const response = await api.get(`/transactions/${selectedCompanyId}?${params.toString()}`);
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, page, startDate, endDate, transactionType, departmentFilter, searchTerm, selectedBankAccount]);

  const loadDepartments = useCallback(async () => {
    if (!selectedCompanyId) return;

    try {
      const response = await api.get(`/departments/${selectedCompanyId}`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  }, [selectedCompanyId]);

  const loadTransactionTypes = useCallback(async () => {
    if (!selectedCompanyId) return;

    try {
      const response = await api.get(`/transactions/${selectedCompanyId}/types`);
      setTransactionTypes(response.data.transaction_types);
    } catch (error) {
      console.error('Failed to load transaction types:', error);
    }
  }, [selectedCompanyId]);

  const loadBankAccounts = useCallback(async () => {
    if (!selectedCompanyId) return;

    try {
      const response = await api.get(`/transactions/${selectedCompanyId}/bank-accounts`);
      setBankAccounts(response.data.bank_accounts);
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (selectedCompanyId) {
      loadTransactions();
      loadDepartments();
      loadTransactionTypes();
      loadBankAccounts();
    }
  }, [selectedCompanyId, loadTransactions, loadDepartments, loadTransactionTypes, loadBankAccounts]);

  // Reset filters and page when company changes
  useEffect(() => {
    setPage(1);
    setStartDate('');
    setEndDate('');
    setTransactionType('');
    setDepartmentFilter('');
    setSearchTerm('');
    setSelectedBankAccount('');
  }, [selectedCompanyId]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, transactionType, departmentFilter, searchTerm, selectedBankAccount]);

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    const company = companies.find(c => c.company_id === companyId);
    setSelectedCompanyName(company?.company_name || '');
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setTransactionType('');
    setDepartmentFilter('');
    setSearchTerm('');
    setSelectedBankAccount('');
    setPage(1);
  };

  // Start inline editing
  const startEditing = (transaction) => {
    setEditingId(transaction.id);
    setEditDepartment(transaction.department_id || '');
    setEditComment(transaction.user_comment || '');
  };

  // Cancel inline editing
  const cancelEditing = () => {
    setEditingId(null);
    setEditDepartment('');
    setEditComment('');
  };

  // Save inline edit
  const saveEdit = async (transactionId) => {
    try {
      const updateData = {};
      if (editDepartment !== '') {
        updateData.department_id = parseInt(editDepartment);
      }
      updateData.user_comment = editComment;

      await api.put(`/transactions/${selectedCompanyId}/${transactionId}`, updateData);

      // Update local state
      setTransactions(prev => prev.map(t =>
        t.id === transactionId
          ? {
              ...t,
              department_id: editDepartment ? parseInt(editDepartment) : null,
              department_name: departments.find(d => d.id === parseInt(editDepartment))?.name || null,
              user_comment: editComment
            }
          : t
      ));

      setEditingId(null);
    } catch (error) {
      alert('Failed to update: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Quick department update (without entering edit mode)
  const quickUpdateDepartment = async (transactionId, deptId) => {
    try {
      const updateData = { department_id: deptId ? parseInt(deptId) : null };
      await api.put(`/transactions/${selectedCompanyId}/${transactionId}`, updateData);

      setTransactions(prev => prev.map(t =>
        t.id === transactionId
          ? {
              ...t,
              department_id: deptId ? parseInt(deptId) : null,
              department_name: departments.find(d => d.id === parseInt(deptId))?.name || null
            }
          : t
      ));
    } catch (error) {
      alert('Failed to update department: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Create new department inline
  const handleCreateDepartment = async () => {
    if (!newDeptName.trim()) return;

    try {
      const response = await api.post(`/departments/${selectedCompanyId}`, {
        name: newDeptName.trim()
      });

      setDepartments(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewDeptName('');
      setShowNewDeptInput(false);
    } catch (error) {
      alert('Failed to create department: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Start editing department name
  const startEditingDept = (dept) => {
    setEditingDeptId(dept.id);
    setEditDeptName(dept.name);
  };

  // Cancel editing department
  const cancelEditingDept = () => {
    setEditingDeptId(null);
    setEditDeptName('');
  };

  // Save department name edit
  const handleUpdateDepartment = async () => {
    if (!editDeptName.trim() || !editingDeptId) return;

    try {
      const response = await api.put(`/departments/${selectedCompanyId}/${editingDeptId}`, {
        name: editDeptName.trim()
      });

      setDepartments(prev =>
        prev.map(d => d.id === editingDeptId ? response.data : d)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setEditingDeptId(null);
      setEditDeptName('');
    } catch (error) {
      alert('Failed to update department: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Load department analytics
  const loadDepartmentAnalytics = useCallback(async () => {
    if (!selectedCompanyId) return;

    setAnalyticsLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedBankAccount) params.append('account_name', selectedBankAccount);

      const response = await api.get(`/transactions/${selectedCompanyId}/department-summary?${params.toString()}`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to load department analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [selectedCompanyId, startDate, endDate, selectedBankAccount]);

  // Load analytics when filters change
  useEffect(() => {
    if (showAnalytics && selectedCompanyId) {
      loadDepartmentAnalytics();
    }
  }, [showAnalytics, selectedCompanyId, startDate, endDate, selectedBankAccount, loadDepartmentAnalytics]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '-';
    const num = parseFloat(amount);
    const formatted = Math.abs(num).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    return num < 0 ? `-${formatted}` : formatted;
  };

  if (companies.length === 0) {
    return (
      <div className="user-transactions">
        <div className="no-companies">
          <p>No companies assigned to your account.</p>
          <p>Please contact an administrator to get access to company data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-transactions">
      {/* Company Selector */}
      <div className="company-selector-row">
        <label>Company:</label>
        <select
          value={selectedCompanyId}
          onChange={handleCompanyChange}
          className="company-select"
        >
          {companies.map(company => (
            <option key={company.company_id} value={company.company_id}>
              {company.company_name}
            </option>
          ))}
        </select>
      </div>

      {/* Department Management Row */}
      <div className="department-management-row">
        <span className="dept-label">Departments:</span>
        <div className="dept-chips">
          {departments.map(dept => (
            editingDeptId === dept.id ? (
              <div key={dept.id} className="edit-dept-inline">
                <input
                  type="text"
                  value={editDeptName}
                  onChange={(e) => setEditDeptName(e.target.value)}
                  placeholder="Department name"
                  className="edit-dept-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleUpdateDepartment();
                    if (e.key === 'Escape') cancelEditingDept();
                  }}
                  autoFocus
                />
                <button onClick={handleUpdateDepartment} className="btn-small btn-save-dept">Save</button>
                <button onClick={cancelEditingDept} className="btn-small btn-cancel-dept">X</button>
              </div>
            ) : (
              <span
                key={dept.id}
                className="dept-chip dept-chip-editable"
                onClick={() => startEditingDept(dept)}
                title="Click to edit"
              >
                {dept.name}
              </span>
            )
          ))}
          {showNewDeptInput ? (
            <div className="new-dept-inline">
              <input
                type="text"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                placeholder="New department name"
                className="new-dept-input"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateDepartment()}
              />
              <button onClick={handleCreateDepartment} className="btn-small btn-add">Add</button>
              <button onClick={() => { setShowNewDeptInput(false); setNewDeptName(''); }} className="btn-small">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowNewDeptInput(true)} className="btn-small btn-add-dept">+ Add</button>
          )}
        </div>
        <button
          onClick={() => setShowAnalytics(!showAnalytics)}
          className={`btn-analytics ${showAnalytics ? 'active' : ''}`}
        >
          {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
        </button>
      </div>

      {/* Bank Account Filter Chips */}
      {bankAccounts.length > 0 && (
        <div className="bank-account-filter-row">
          <span className="filter-label">Bank Account:</span>
          <div className="bank-chips">
            <button
              className={`bank-chip ${selectedBankAccount === '' ? 'active' : ''}`}
              onClick={() => setSelectedBankAccount('')}
            >
              All ({bankAccounts.reduce((sum, acc) => sum + acc.count, 0)})
            </button>
            {bankAccounts.map(account => (
              <button
                key={account.name}
                className={`bank-chip ${selectedBankAccount === account.name ? 'active' : ''}`}
                onClick={() => setSelectedBankAccount(account.name)}
                title={account.name}
              >
                {account.name.length > 20 ? account.name.substring(0, 20) + '...' : account.name} ({account.count})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="transaction-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Type</label>
            <select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
            >
              <option value="">All Types</option>
              {transactionTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Department</label>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="none">Uncategorized</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group filter-search">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search description, memo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-actions">
            <button onClick={clearFilters} className="btn-secondary btn-small">Clear</button>
          </div>
        </div>
      </div>

      {/* Department Analytics Section */}
      {showAnalytics && (
        <div className="department-analytics">
          <div className="analytics-header">
            <h3>Department Analytics</h3>
            {(startDate || endDate || selectedBankAccount) && (
              <span className="analytics-filters-info">
                Filtered: {startDate && `From ${startDate}`} {endDate && `To ${endDate}`} {selectedBankAccount && `| Account: ${selectedBankAccount}`}
              </span>
            )}
          </div>

          {analyticsLoading ? (
            <div className="analytics-loading">Loading analytics...</div>
          ) : analyticsData ? (
            <>
              {/* Summary Cards */}
              <div className="analytics-summary-row">
                <div className="analytics-card">
                  <div className="analytics-card-label">Categorized</div>
                  <div className="analytics-card-value categorized">{analyticsData.total_categorized}</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-card-label">Uncategorized</div>
                  <div className="analytics-card-value uncategorized">{analyticsData.total_uncategorized}</div>
                </div>
                <div className="analytics-card">
                  <div className="analytics-card-label">Total Departments</div>
                  <div className="analytics-card-value">{analyticsData.summary.filter(s => s.department_id).length}</div>
                </div>
              </div>

              {/* Department Summary Table */}
              <div className="analytics-section">
                <h4>Spending by Department</h4>
                <div className="analytics-table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Department</th>
                        <th>Transactions</th>
                        <th>Income</th>
                        <th>Expenses</th>
                        <th>Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.summary.map((item, idx) => (
                        <tr key={idx} className={!item.department_id ? 'uncategorized-row' : ''}>
                          <td className="dept-name-cell">
                            {item.department_name}
                            {!item.department_id && <span className="uncategorized-badge">Uncategorized</span>}
                          </td>
                          <td className="count-cell">{item.transaction_count}</td>
                          <td className="amount positive">{formatAmount(item.total_income)}</td>
                          <td className="amount negative">{formatAmount(-item.total_expenses)}</td>
                          <td className={`amount ${item.net >= 0 ? 'positive' : 'negative'}`}>
                            {formatAmount(item.net)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Weekly Trends */}
              {analyticsData.trends.length > 0 && (
                <div className="analytics-section">
                  <h4>Weekly Trends</h4>
                  <div className="trends-container">
                    {/* Group trends by week */}
                    {(() => {
                      const weeklyGroups = {};
                      analyticsData.trends.forEach(t => {
                        const weekKey = t.week_start;
                        if (!weeklyGroups[weekKey]) {
                          weeklyGroups[weekKey] = {
                            week_start: t.week_start,
                            week_end: t.week_end,
                            departments: []
                          };
                        }
                        weeklyGroups[weekKey].departments.push(t);
                      });

                      const weeks = Object.values(weeklyGroups).slice(0, 8); // Last 8 weeks

                      return (
                        <div className="trends-grid">
                          {weeks.map((week, idx) => (
                            <div key={idx} className="trend-week-card">
                              <div className="trend-week-header">
                                {new Date(week.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(week.week_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                              <div className="trend-week-depts">
                                {week.departments.slice(0, 5).map((dept, dIdx) => (
                                  <div key={dIdx} className="trend-dept-row">
                                    <span className="trend-dept-name">{dept.department_name}</span>
                                    <span className={`trend-dept-amount ${dept.net >= 0 ? 'positive' : 'negative'}`}>
                                      {formatAmount(dept.net)}
                                    </span>
                                  </div>
                                ))}
                                {week.departments.length > 5 && (
                                  <div className="trend-dept-more">+{week.departments.length - 5} more</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="analytics-empty">No analytics data available</div>
          )}
        </div>
      )}

      {/* Results info */}
      <div className="transaction-info">
        {total} transactions
        {(startDate || endDate || transactionType || departmentFilter || searchTerm || selectedBankAccount) &&
          <span className="filter-active"> (filtered)</span>
        }
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading">Loading transactions...</div>
      ) : (
        <div className="transaction-table-container">
          <table className="data-table transaction-table">
            <thead>
              <tr>
                <th style={{width: '90px'}}>Date</th>
                <th style={{width: '80px'}}>Type</th>
                <th style={{width: '100px'}}>Amount</th>
                <th style={{width: '150px'}}>Bank Account</th>
                <th className="desc-col">Description</th>
                <th style={{width: '130px'}}>Department</th>
                <th style={{width: '150px'}}>Comment</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    No transactions found for this company.
                  </td>
                </tr>
              ) : (
                transactions.map(transaction => (
                  <tr key={transaction.id} className={editingId === transaction.id ? 'editing-row' : ''}>
                    <td>{formatDate(transaction.transaction_date)}</td>
                    <td>
                      <span className={`type-badge type-${transaction.transaction_type?.toLowerCase()}`}>
                        {transaction.transaction_type || '-'}
                      </span>
                    </td>
                    <td className={`amount ${parseFloat(transaction.amount) < 0 ? 'negative' : 'positive'}`}>
                      {formatAmount(transaction.amount)}
                    </td>
                    <td className="account-cell" title={transaction.account_name}>
                      {transaction.account_name || '-'}
                    </td>
                    <td className="desc-cell" title={transaction.bank_description || transaction.memo}>
                      {transaction.bank_description || transaction.memo || '-'}
                    </td>
                    <td className="dept-cell">
                      {editingId === transaction.id ? (
                        <select
                          value={editDepartment}
                          onChange={(e) => setEditDepartment(e.target.value)}
                          className="inline-select"
                        >
                          <option value="">None</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                          ))}
                        </select>
                      ) : (
                        <select
                          value={transaction.department_id || ''}
                          onChange={(e) => quickUpdateDepartment(transaction.id, e.target.value)}
                          className="inline-select"
                        >
                          <option value="">-</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="comment-cell">
                      {editingId === transaction.id ? (
                        <div className="edit-comment-row">
                          <input
                            type="text"
                            value={editComment}
                            onChange={(e) => setEditComment(e.target.value)}
                            className="inline-input"
                            placeholder="Add comment..."
                            onKeyDown={(e) => e.key === 'Enter' && saveEdit(transaction.id)}
                          />
                          <button onClick={() => saveEdit(transaction.id)} className="btn-inline btn-save">Save</button>
                          <button onClick={cancelEditing} className="btn-inline btn-cancel">X</button>
                        </div>
                      ) : (
                        <div className="comment-display" onClick={() => startEditing(transaction)}>
                          {transaction.user_comment || <span className="add-comment">+ Add</span>}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className="btn-small"
          >
            First
          </button>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-small"
          >
            Prev
          </button>
          <span className="page-info">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-small"
          >
            Next
          </button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            className="btn-small"
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
}

export default UserTransactions;
